import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentPlusIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@components/PageHeader';
import { Card } from '@components/ui/Card';
import { Button } from '@components/ui/Button';
import { Spinner } from '@components/ui/Spinner';
import { StageManager } from '@components/StageManager';
import { useEstateStore, EstateMetadata } from '../../stores/estateStore';
import { fetchPublicEstates, BlocPlusEstate } from '@services/blocPlusApi';
import type { EstateItem, EstateJsonData, EstateStageJson } from '@types';
import {
  blocPlusToItems,
  blocPlusToMetadata,
  jsonDataToItems,
  jsonDataToMetadata,
  mergeItems,
  mergeEstateStages,
  lotCount,
  refCount,
} from '../../utils/mergeEstates';
import { validateStageConfiguration } from '../../utils/stages';

type Mode = 'blocplus' | 'local';

interface ParsedFile {
  id: string;
  name: string;
  items: EstateItem[];
  metadata: EstateMetadata;
  error?: string;
}

async function parseFile(file: File): Promise<ParsedFile> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const id = `${file.name}-${Date.now()}-${Math.random()}`;
      try {
        const json = JSON.parse(e.target?.result as string) as EstateJsonData;
        if (!json.lots) throw new Error('No lots array found');
        resolve({
          id,
          name: file.name,
          items: jsonDataToItems(json),
          metadata: jsonDataToMetadata(json),
        });
      } catch {
        resolve({
          id,
          name: file.name,
          items: [],
          metadata: {
            planNumber: '',
            name: file.name,
            stage: '',
            address: '',
            status: 'draft',
            geolocation: { lat: 0, lng: 0 },
          },
          error: 'Invalid JSON or unrecognised BlocPlus format',
        });
      }
    };
    reader.readAsText(file);
  });
}

export const MergePlans = () => {
  const navigate = useNavigate();
  const { loadEstateWithItems } = useEstateStore();

  const [mode, setMode] = useState<Mode>('blocplus');

  // ── Mode A state ──────────────────────────────────────────────────────────
  const [estates, setEstates] = useState<BlocPlusEstate[]>([]);
  const [loadingEstates, setLoadingEstates] = useState(false);
  const [estateError, setEstateError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedEstate, setSelectedEstate] = useState<BlocPlusEstate | null>(null);
  const [addFiles, setAddFiles] = useState<ParsedFile[]>([]);
  const [modeAStages, setModeAStages] = useState<EstateStageJson[]>([]);
  const addFileRef = useRef<HTMLInputElement>(null);

  // ── Mode B state ──────────────────────────────────────────────────────────
  const [localFiles, setLocalFiles] = useState<ParsedFile[]>([]);
  const [mergeName, setMergeName] = useState('');
  const [modeBStages, setModeBStages] = useState<EstateStageJson[]>([]);
  const localFileRef = useRef<HTMLInputElement>(null);

  // Load BlocPlus estates on first switch to Mode A
  useEffect(() => {
    if (mode !== 'blocplus' || estates.length > 0) return;
    let cancelled = false;
    setLoadingEstates(true);
    setEstateError(null);
    fetchPublicEstates()
      .then((data) => {
        if (!cancelled) setEstates(data);
      })
      .catch((err) => {
        if (!cancelled) setEstateError(err instanceof Error ? err.message : 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoadingEstates(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, estates.length, retryCount]);

  // Auto-fill merge name from first local file
  useEffect(() => {
    if (localFiles.length > 0 && !mergeName) {
      setMergeName(localFiles[0].metadata.name || localFiles[0].name.replace(/\.json$/i, ''));
    }
  }, [localFiles, mergeName]);

  const filteredEstates = useMemo(() => {
    const f = searchFilter.toLowerCase();
    const sorted = [...estates].sort((a, b) => a.name.localeCompare(b.name));
    return f ? sorted.filter((e) => e.name.toLowerCase().includes(f)) : sorted;
  }, [estates, searchFilter]);

  // ── Computed base items for Mode A ────────────────────────────────────────
  const baseItems = useMemo(
    () => (selectedEstate ? blocPlusToItems(selectedEstate) : []),
    [selectedEstate]
  );

  // ── Merge previews ────────────────────────────────────────────────────────
  const modeAPreview = useMemo(() => {
    const validAdditions = addFiles.filter((f) => !f.error).map((f) => f.items);
    if (baseItems.length === 0 || validAdditions.length === 0) return null;
    return mergeItems(baseItems, validAdditions);
  }, [baseItems, addFiles]);

  const modeBPreview = useMemo(() => {
    const valid = localFiles.filter((f) => !f.error);
    if (valid.length < 2) return null;
    return mergeItems(
      valid[0].items,
      valid.slice(1).map((f) => f.items)
    );
  }, [localFiles]);

  const modeAStageDefaults = useMemo(() => {
    if (!selectedEstate || !modeAPreview) return [];
    return mergeEstateStages(
      [
        blocPlusToMetadata(selectedEstate),
        ...addFiles.filter((file) => !file.error).map((file) => file.metadata),
      ],
      modeAPreview.items
    );
  }, [addFiles, modeAPreview, selectedEstate]);

  const modeBStageDefaults = useMemo(() => {
    if (!modeBPreview) return [];
    return mergeEstateStages(
      localFiles.filter((file) => !file.error).map((file) => file.metadata),
      modeBPreview.items
    );
  }, [localFiles, modeBPreview]);

  useEffect(() => setModeAStages(modeAStageDefaults), [modeAStageDefaults]);
  useEffect(() => setModeBStages(modeBStageDefaults), [modeBStageDefaults]);

  const modeAStageValidation = validateStageConfiguration(
    modeAStages,
    modeAPreview?.items.filter((item) => item.classification === 'lot') ?? []
  );
  const modeBStageValidation = validateStageConfiguration(
    modeBStages,
    modeBPreview?.items.filter((item) => item.classification === 'lot') ?? []
  );

  // ── File handlers ─────────────────────────────────────────────────────────
  const handleAddFiles = async (files: FileList | null) => {
    if (!files) return;
    const parsed = await Promise.all(Array.from(files).map(parseFile));
    setAddFiles((prev) => [...prev, ...parsed]);
  };

  const handleLocalFiles = async (files: FileList | null) => {
    if (!files) return;
    const parsed = await Promise.all(Array.from(files).map(parseFile));
    setLocalFiles((prev) => [...prev, ...parsed]);
  };

  const moveFile = (index: number, direction: 'up' | 'down') => {
    setLocalFiles((prev) => {
      const next = [...prev];
      const swap = direction === 'up' ? index - 1 : index + 1;
      [next[index], next[swap]] = [next[swap], next[index]];
      return next;
    });
  };

  // ── Merge action ──────────────────────────────────────────────────────────
  const handleMerge = () => {
    let metadata: EstateMetadata;
    let mergedItems: EstateItem[];

    if (mode === 'blocplus' && selectedEstate && modeAPreview) {
      mergedItems = modeAPreview.items;
      const sourceMetadata = [
        blocPlusToMetadata(selectedEstate),
        ...addFiles.filter((file) => !file.error).map((file) => file.metadata),
      ];
      const stages = modeAStages;
      metadata = {
        ...sourceMetadata[0],
        stage: stages.map((stage) => stage.name).join(', '),
        stages,
      };
    } else if (mode === 'local' && modeBPreview) {
      const valid = localFiles.filter((f) => !f.error);
      const resolvedName = mergeName || valid[0].metadata.name;
      mergedItems = modeBPreview.items;
      const stages = modeBStages;
      metadata = {
        ...valid[0].metadata,
        name: resolvedName,
        planNumber: resolvedName,
        stage: stages.map((stage) => stage.name).join(', '),
        stages,
      };
    } else {
      return;
    }

    loadEstateWithItems(metadata, mergedItems);
    navigate(`/edit/${encodeURIComponent(metadata.name || 'merged')}`, {
      state: { preloaded: true },
    });
  };

  const canMergeA =
    selectedEstate !== null && modeAPreview !== null && modeAStageValidation.errors.length === 0;
  const canMergeB =
    localFiles.filter((f) => !f.error).length >= 2 &&
    modeBPreview !== null &&
    mergeName.trim() !== '' &&
    modeBStageValidation.errors.length === 0;

  // ── Shared sub-components ─────────────────────────────────────────────────
  const MergePreviewCard = ({
    baseLabel,
    baseLots,
    baseRefs,
    additions,
    preview,
  }: {
    baseLabel: string;
    baseLots: number;
    baseRefs: number;
    additions: { label: string; lots: number; refs: number }[];
    preview: { items: EstateItem[]; skipped: number } | null;
  }) => (
    <Card>
      <h3 className="mb-4 text-xl font-bold text-gray-800">Merge Preview</h3>
      <div className="space-y-3">
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-xs font-bold uppercase text-gray-500">Base</p>
          <p className="text-sm font-medium text-gray-800">{baseLabel}</p>
          <p className="text-xs text-gray-500">
            {baseLots} lots · {baseRefs} references
          </p>
        </div>

        {additions.map((a, i) => (
          <div key={i} className="rounded-lg bg-green-50 p-3">
            <p className="text-xs font-bold uppercase text-green-700">Adding</p>
            <p className="text-sm font-medium text-gray-800">{a.label}</p>
            <p className="text-xs text-gray-500">
              {a.lots} lots · {a.refs} references
            </p>
          </div>
        ))}

        {preview && (
          <div className="border-t border-gray-200 pt-3">
            <p className="text-xs font-bold uppercase text-gray-500">Result</p>
            <p className="text-sm font-medium text-gray-800">
              {lotCount(preview.items)} lots · {refCount(preview.items)} references
            </p>
            {preview.skipped > 0 && (
              <p className="mt-1 text-xs text-amber-600">
                {preview.skipped} duplicate code{preview.skipped !== 1 ? 's' : ''} skipped —
                existing lots kept
              </p>
            )}
          </div>
        )}
      </div>
    </Card>
  );

  const StageSetupCard = ({
    title,
    lots,
    stages,
    onChange,
    canMerge,
  }: {
    title: string;
    lots: EstateItem[];
    stages: EstateStageJson[];
    onChange: (stages: EstateStageJson[]) => void;
    canMerge: boolean;
  }) => (
    <Card>
      <h3 className="mb-1 text-xl font-bold text-gray-800">{title}</h3>
      <p className="mb-4 text-xs text-gray-500">
        Group merged lots into stages and set their release behaviour before review.
      </p>
      <StageManager lots={lots} stages={stages} onChange={onChange} />
      <Button onClick={handleMerge} disabled={!canMerge} className="mt-5 w-full">
        <ArrowsRightLeftIcon className="h-4 w-4" />
        Merge &amp; Review
      </Button>
    </Card>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Merge Plans"
        subtitle="Combine plan of subdivision files into a single estate"
        showBackButton
      />

      {/* Mode tabs */}
      <div className="mb-6 flex gap-2 border-b border-gray-200 pb-0">
        {(
          [
            { key: 'blocplus', label: 'Merge into BlocPlus Estate' },
            { key: 'local', label: 'Combine Local Files' },
          ] as { key: Mode; label: string }[]
        ).map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setMode(key)}
            className={`-mb-px border-b-2 px-4 py-2 text-s font-bold transition-colors focus:outline-none ${
              mode === key
                ? 'border-gold text-gold'
                : 'border-transparent text-gray-600 hover:text-gold'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── MODE A ─────────────────────────────────────────────────────────── */}
      {mode === 'blocplus' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            {/* Step 1 — select target estate */}
            <Card>
              <h3 className="mb-1 text-xl font-bold text-gray-800">
                Step 1 — Select Target Estate
              </h3>
              <p className="mb-4 text-xs text-gray-500">
                Choose the existing BlocPlus estate to merge into.
              </p>

              {loadingEstates ? (
                <div className="flex items-center gap-3 py-6">
                  <Spinner />
                  <span className="text-sm text-gray-500">Loading BlocPlus estates…</span>
                </div>
              ) : estateError ? (
                <div className="py-4 text-center">
                  <p className="mb-3 text-sm text-red-600">{estateError}</p>
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => {
                      setEstates([]);
                      setEstateError(null);
                      setRetryCount((n) => n + 1);
                    }}
                  >
                    Retry
                  </Button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder="Filter by name…"
                    className="mb-3 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <div className="max-h-80 space-y-1.5 overflow-y-auto">
                    {filteredEstates.map((estate) => {
                      const key = estate.id || estate.name;
                      const isSelected = selectedEstate?.id === estate.id;
                      return (
                        <div
                          key={key}
                          onClick={() => setSelectedEstate(estate)}
                          className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors hover:bg-gray-50 ${
                            isSelected ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'
                          }`}
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-800">{estate.name}</p>
                            <p className="text-xs text-gray-500">
                              {estate.totalLotsCount ?? estate.lots?.length ?? 0} lots
                            </p>
                          </div>
                          {isSelected && (
                            <span className="text-xs font-bold text-yellow-700">Selected ✓</span>
                          )}
                        </div>
                      );
                    })}
                    {filteredEstates.length === 0 && !loadingEstates && (
                      <p className="py-4 text-center text-sm text-gray-400">No estates match</p>
                    )}
                  </div>
                </>
              )}
            </Card>

            {/* Step 2 — upload files to add */}
            <Card>
              <h3 className="mb-1 text-xl font-bold text-gray-800">Step 2 — Upload Plans to Add</h3>
              <p className="mb-4 text-xs text-gray-500">
                Upload one or more BlocPlus JSON files whose lots will be merged in.
              </p>

              <div
                onClick={() => addFileRef.current?.click()}
                className="mb-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 text-center transition-colors hover:border-yellow-400 hover:bg-yellow-50"
              >
                <DocumentPlusIcon className="mb-2 h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-600">Click to upload JSON files</p>
                <p className="text-xs text-gray-400">BlocPlus format · multiple allowed</p>
              </div>
              <input
                ref={addFileRef}
                type="file"
                accept=".json"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleAddFiles(e.target.files);
                  e.target.value = '';
                }}
              />

              {addFiles.length > 0 && (
                <div className="space-y-2">
                  {addFiles.map((file) => (
                    <div
                      key={file.id}
                      className={`flex items-center justify-between rounded-lg border p-3 ${
                        file.error ? 'border-red-200 bg-red-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
                        {file.error ? (
                          <p className="text-xs text-red-600">{file.error}</p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            {lotCount(file.items)} lots · {refCount(file.items)} references
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setAddFiles((prev) => prev.filter((f) => f.id !== file.id))}
                        className="ml-3 flex-shrink-0 rounded p-1 text-gray-400 hover:text-red-500"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Right: preview */}
          <div className="space-y-6">
            {selectedEstate && modeAPreview ? (
              <>
                <MergePreviewCard
                  baseLabel={selectedEstate.name}
                  baseLots={lotCount(baseItems)}
                  baseRefs={refCount(baseItems)}
                  additions={addFiles
                    .filter((f) => !f.error)
                    .map((f) => ({
                      label: f.name,
                      lots: lotCount(f.items),
                      refs: refCount(f.items),
                    }))}
                  preview={modeAPreview}
                />
                <StageSetupCard
                  title="Step 3 — Set Up Stages"
                  lots={modeAPreview.items.filter((item) => item.classification === 'lot')}
                  stages={modeAStages}
                  onChange={setModeAStages}
                  canMerge={canMergeA}
                />
              </>
            ) : (
              <div className="flex h-48 items-center justify-center rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-sm text-gray-400">
                  {!selectedEstate
                    ? 'Select a BlocPlus estate to begin'
                    : 'Upload at least one plan file to preview'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODE B ─────────────────────────────────────────────────────────── */}
      {mode === 'local' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            {/* File upload */}
            <Card>
              <h3 className="mb-1 text-xl font-bold text-gray-800">Upload Files to Combine</h3>
              <p className="mb-4 text-xs text-gray-500">
                The first file is the base — its lot codes win any conflicts. Use the arrows to
                reorder.
              </p>

              <div
                onClick={() => localFileRef.current?.click()}
                className="mb-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 text-center transition-colors hover:border-yellow-400 hover:bg-yellow-50"
              >
                <DocumentPlusIcon className="mb-2 h-8 w-8 text-gray-400" />
                <p className="text-sm text-gray-600">Click to upload JSON files</p>
                <p className="text-xs text-gray-400">Select multiple at once or add one by one</p>
              </div>
              <input
                ref={localFileRef}
                type="file"
                accept=".json"
                multiple
                className="hidden"
                onChange={(e) => {
                  handleLocalFiles(e.target.files);
                  e.target.value = '';
                }}
              />

              {localFiles.length > 0 && (
                <div className="space-y-2">
                  {localFiles.map((file, index) => (
                    <div
                      key={file.id}
                      className={`flex items-center gap-2 rounded-lg border p-3 ${
                        file.error
                          ? 'border-red-200 bg-red-50'
                          : index === 0
                            ? 'border-yellow-400 bg-yellow-50'
                            : 'border-gray-200'
                      }`}
                    >
                      {/* Reorder controls */}
                      <div className="flex flex-shrink-0 flex-col gap-0.5">
                        <button
                          disabled={index === 0}
                          onClick={() => moveFile(index, 'up')}
                          className="rounded p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20"
                        >
                          <ChevronUpIcon className="h-3.5 w-3.5" />
                        </button>
                        <button
                          disabled={index === localFiles.length - 1}
                          onClick={() => moveFile(index, 'down')}
                          className="rounded p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-20"
                        >
                          <ChevronDownIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* File info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {index === 0 && (
                            <span className="rounded-full bg-yellow-400 px-1.5 py-0.5 text-[10px] font-bold text-yellow-900">
                              BASE
                            </span>
                          )}
                          <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
                        </div>
                        {file.error ? (
                          <p className="text-xs text-red-600">{file.error}</p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            {lotCount(file.items)} lots · {refCount(file.items)} references
                          </p>
                        )}
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() =>
                          setLocalFiles((prev) => prev.filter((f) => f.id !== file.id))
                        }
                        className="ml-1 flex-shrink-0 rounded p-1 text-gray-400 hover:text-red-500"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Merged estate name */}
            {localFiles.length >= 1 && (
              <Card>
                <h3 className="mb-3 text-xl font-bold text-gray-800">Merged Estate Name</h3>
                <input
                  type="text"
                  value={mergeName}
                  onChange={(e) => setMergeName(e.target.value)}
                  placeholder="Enter a name for the merged estate"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </Card>
            )}
          </div>

          {/* Right: preview */}
          <div className="space-y-6">
            {localFiles.filter((f) => !f.error).length >= 2 && modeBPreview ? (
              <>
                <MergePreviewCard
                  baseLabel={localFiles[0].name}
                  baseLots={lotCount(localFiles[0].items)}
                  baseRefs={refCount(localFiles[0].items)}
                  additions={localFiles
                    .slice(1)
                    .filter((f) => !f.error)
                    .map((f) => ({
                      label: f.name,
                      lots: lotCount(f.items),
                      refs: refCount(f.items),
                    }))}
                  preview={modeBPreview}
                />
                <StageSetupCard
                  title="Set Up Stages"
                  lots={modeBPreview.items.filter((item) => item.classification === 'lot')}
                  stages={modeBStages}
                  onChange={setModeBStages}
                  canMerge={canMergeB}
                />
              </>
            ) : (
              <div className="flex h-48 items-center justify-center rounded-lg border-2 border-dashed border-gray-200">
                <p className="text-sm text-gray-400">
                  {localFiles.filter((f) => !f.error).length < 2
                    ? 'Upload at least two files to preview'
                    : 'Enter an estate name to merge'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
